/**
 * Tipos de archivo permitidos para el documento del informe de cumplimiento.
 *
 * Fuente única compartida por `SeccionEnviarInformeComponent` y
 * `EnviarInformeDialogComponent`, para evitar que las dos listas diverjan.
 *
 * Backend: `ArchivoInformeValidator.EXTENSIONES_PERMITIDAS_INFORME`
 * (`domain/model/.../informecumplimiento/ArchivoInformeValidator.java`).
 * Legacy: el informe no valida extensión (ver `EnviarInformeViewImpl`); esta
 * lista es la mejora defensiva ya existente, ampliada con `.zip` y `.rar` para
 * permitir empaquetar varios soportes en un solo archivo.
 */

/** MIME types que acepta el input de archivo del informe. */
export const TIPOS_ARCHIVO_PERMITIDOS_INFORME: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  // El navegador reporta el ZIP con distintos MIME según el sistema operativo;
  // por eso además se valida por extensión en onArchivoSeleccionado().
  'application/zip',
  'application/x-zip-compressed',
  'application/x-zip',
  // Igual que ZIP: el MIME de RAR varía según navegador y sistema operativo.
  'application/vnd.rar',
  'application/x-rar-compressed',
  'application/x-rar'
];

/** Extensiones que acepta el input de archivo del informe (con el punto). */
export const EXTENSIONES_ACEPTADAS_INFORME =
  '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.zip,.rar';

/** Extensiones permitidas para mostrar al usuario y en mensajes de error. */
export const EXTENSIONES_PERMITIDAS_INFORME =
  'PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, PPT, PPTX, CSV, ZIP, RAR';

/** Tamaño máximo del archivo del informe en bytes (10 MB, igual que el backend). */
export const MAX_FILE_SIZE_INFORME = 10 * 1024 * 1024;

/**
 * Extrae la extensión de un nombre de archivo, en minúsculas y sin el punto.
 * Devuelve `null` si no tiene extensión.
 */
function extensionDe(nombreArchivo: string): string | null {
  const idx = nombreArchivo.lastIndexOf('.');
  if (idx < 0 || idx === nombreArchivo.length - 1) {
    return null;
  }
  return nombreArchivo.substring(idx + 1).toLowerCase();
}

const EXTENSIONES_VALIDAS = new Set(
  EXTENSIONES_ACEPTADAS_INFORME.split(',').map((ext) => ext.replace('.', ''))
);

/**
 * Determina si un archivo es un tipo permitido para el informe.
 *
 * Valida por `file.type` cuando el navegador lo reporta, y siempre por
 * extensión: en Windows, un `.zip` puede llegar con `file.type` vacío o con
 * `application/x-zip-compressed`, así que no basta con el MIME. La validación
 * fuerte (firma binaria, integridad del ZIP) la hace el backend
 * (`ArchivoInformeValidator`); esto es solo feedback rápido en el cliente.
 */
export function esTipoArchivoInformePermitido(file: File): boolean {
  const extension = extensionDe(file.name);
  if (extension && EXTENSIONES_VALIDAS.has(extension)) {
    return true;
  }
  return TIPOS_ARCHIVO_PERMITIDOS_INFORME.includes(file.type);
}
